import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Check, Users, Timer, TrendingUp, Coffee, BookOpen, Briefcase, Video, BookCheck, UsersRound, CheckSquare, Bell, Sparkles } from "lucide-react";

// Hero Section
const HeroVi = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
      
      {/* Animated Glow Effects */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left space-y-8 animate-fade-in">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-gradient">Phòng học ảo</span>
            </h1>
            
            <p className="text-xl sm:text-2xl font-light max-w-xl">
              Đồng Hành Cùng Bạn Trong Hành Trình Học Tập
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold max-w-xl">
              Một Mình<br />
              Nhưng Không Bao Giờ Cô Đơn
            </h2>

            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
              CSW mang đến một cách tiếp cận hoàn toàn mới cho việc học trực tuyến thông qua phòng học ảo toàn diện, 
              được thiết kế dành riêng cho học viên trên toàn thế giới. Dù bạn đang chuẩn bị cho kỳ thi, xử lý các dự án lớn, 
              hay đơn giản chỉ cần một môi trường học tập hiệu quả, CSW chính là nền tảng lý tưởng để bạn học tập cùng những 
              người bạn đồng chí hướng đến từ khắp nơi.
            </p>
            
            <div className="pt-4">
              <Button 
                size="lg" 
                className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Join A Room Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
            
            <div className="flex items-center gap-8 pt-8">
              <div>
                <p className="text-3xl font-bold text-gradient">100k+</p>
                <p className="text-sm text-muted-foreground">Hours Completed</p>
              </div>
              <div className="w-px h-12 bg-border" />
              <div>
                <p className="text-3xl font-bold text-gradient">8k+</p>
                <p className="text-sm text-muted-foreground">Task Completed</p>
              </div>
            </div>
          </div>
          
          {/* Right Video */}
          <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
              <video 
                src="/videos/productivity-demo.mp4" 
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            </div>
            
            {/* Floating Stats Card */}
            <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl animate-slide-in" style={{ animationDelay: '0.4s' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Đang Trực Tuyến</p>
                  <p className="text-2xl font-bold text-foreground">554 học viên</p>
                </div>
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-card" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Meet New People Section
const MeetNewPeopleVi = () => {
  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background to-primary/5">
      <div className="max-w-6xl mx-auto">
        <Card className="p-12 text-center bg-card/80 backdrop-blur border-primary/30">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            GẶP GỠ <span className="text-primary">BẠN MỚI</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Không áp lực giao tiếp.
          </p>
          <Button size="lg" variant="default" asChild>
            <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
              LET'S GO!
            </a>
          </Button>
        </Card>
      </div>
    </section>
  );
};

// Productivity Tools Section
const ProductivityToolsVi = () => {
  return (
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Timer className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-primary">Pomodoro Timer</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Công Cụ Hỗ Trợ Năng Suất
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Quản lý khối lượng công việc và hoàn thành nhiều hơn với Pomodoro Timer
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Đã đến lúc học tập hiệu quả hơn! CSW cung cấp đầy đủ các công cụ bạn cần, bao gồm Pomodoro Timer 
              để quản lý thời gian học, tăng cường tập trung và nâng cao hiệu suất trong mỗi buổi học.
            </p>
          </div>
          <Card className="p-8 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
            <div className="aspect-video bg-background/50 rounded-lg flex items-center justify-center">
              <Timer className="w-24 h-24 text-primary/50" />
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

// 24/7 Support Section
const SupportVi = () => {
  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background to-accent/5">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">
          Hỗ Trợ Học Tập 24/7
        </h2>
        <p className="text-xl text-center text-muted-foreground mb-12">
          Được Tùy Chỉnh Theo Nhu Cầu Của Bạn
        </p>
        
        <Card className="p-8 md:p-12 bg-card/80 backdrop-blur">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Phòng học ảo của CSW mở cửa 24/7, cung cấp tài nguyên, hỗ trợ cộng đồng và các buổi học nhóm tương tác, 
            đảm bảo bạn luôn được đồng hành. Từ việc tìm kiếm bạn học để duy trì trách nhiệm, nhận hướng dẫn từ gia sư, 
            đến những giờ giải lao ý nghĩa, CSW đều đáp ứng tất cả. Chúng tôi mang đến các mục tiêu học tập rõ ràng, 
            bộ hẹn giờ tùy chỉnh và công cụ theo dõi tiến độ chi tiết để bạn duy trì động lực. Với các buổi học gamified 
            và phần thưởng tích hợp, chúng tôi biến việc học trở thành một trải nghiệm thú vị và bổ ích. Không giống như 
            các nền tảng khác thường xuyên bị gián đoạn, CSW mang đến một hành trình học tập mượt mà và không ngắt quãng.
          </p>
        </Card>
      </div>
    </section>
  );
};

// Virtual Classroom Models Section
const ClassroomModelsVi = () => {
  return (
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
          Mô Hình Lớp Học Ảo Hiện Đại
        </h2>
        <p className="text-lg text-muted-foreground mb-12 text-center max-w-4xl mx-auto">
          CSW áp dụng các mô hình lớp học ảo tiên tiến để đáp ứng nhu cầu học tập đa dạng của học viên. 
          Một số mô hình nổi bật bao gồm:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              title: "Mô hình phòng học trực tuyến hoàn toàn",
              description: "Toàn bộ quá trình học diễn ra trên nền tảng ảo, tích hợp đầy đủ các công cụ hỗ trợ học tập."
            },
            {
              title: "Lớp học đảo ngược",
              description: "Học viên chuẩn bị bài trước qua tài liệu hoặc video, dành thời gian trong lớp để thảo luận và giải quyết vấn đề thực tiễn."
            },
            {
              title: "Mô hình quay tròn",
              description: "Phối hợp giữa các buổi học trực tuyến và các buổi học nhóm tự quản, giúp tăng cường tính tương tác."
            },
            {
              title: "Mô hình nâng cao",
              description: "Kết hợp công nghệ đám mây và hệ thống quản lý học tập để tạo nên trải nghiệm học tập chất lượng cao."
            }
          ].map((model, index) => (
            <Card key={index} className="p-6 hover:border-primary/50 transition-colors">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Check className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{model.title}</h3>
                  <p className="text-muted-foreground">{model.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Software Section
const SoftwareVi = () => {
  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background to-primary/5">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
          Phần Mềm Lớp Học Ảo Phổ Biến
        </h2>
        <p className="text-lg text-muted-foreground mb-12 text-center max-w-4xl mx-auto">
          CSW tích hợp dễ dàng với các phần mềm hàng đầu, mang đến trải nghiệm học tập liền mạch:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            "Zoom và Google Meet: Tối ưu cho các buổi học trực tuyến với tính năng chia sẻ màn hình và phòng thảo luận nhóm.",
            "Google Classroom: Quản lý tài liệu học tập và giao bài tập dễ dàng.",
            "ClassIn và Adobe Connect: Tích hợp bảng trắng ảo và các công cụ tương tác trực tiếp.",
            "BigBlueButton và Kaltura: Lý tưởng cho các buổi giảng dạy với khả năng ghi hình và lưu trữ bài giảng."
          ].map((software, index) => (
            <Card key={index} className="p-6 hover:border-accent/50 transition-colors">
              <p className="text-muted-foreground leading-relaxed">{software}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Alone Section
const AloneSectionVi = () => {
  return (
    <section className="py-20 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-background" />
      
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <h2 className="text-5xl md:text-6xl font-bold mb-8">
          Một Mình, Nhưng Không Bao Giờ Cô Đơn
        </h2>
        <div className="flex items-center justify-center gap-8 text-2xl md:text-3xl text-muted-foreground">
          <span>Chúng Tôi</span>
          <span className="text-4xl md:text-5xl font-bold text-primary">❤️</span>
          <span>Thời Gian Yên Tĩnh</span>
        </div>
      </div>
    </section>
  );
};

// Criteria Section
const CriteriaVi = () => {
  return (
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
          Tiêu Chí Chọn Phần Mềm Lớp Học Ảo
        </h2>
        <p className="text-lg text-muted-foreground mb-12 text-center max-w-4xl mx-auto">
          Để lựa chọn phần mềm phù hợp, CSW đưa ra các tiêu chí sau:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              icon: Users,
              title: "Tính năng tương tác cao",
              description: "Hỗ trợ bảng trắng kỹ thuật số, phản hồi tức thì và phòng thảo luận nhóm."
            },
            {
              icon: TrendingUp,
              title: "Hỗ trợ đa thiết bị",
              description: "Phù hợp với máy tính, máy tính bảng và điện thoại thông minh."
            },
            {
              icon: Check,
              title: "Lưu trữ đám mây",
              description: "Dễ dàng truy cập và quản lý tài liệu học tập mọi lúc, mọi nơi."
            },
            {
              icon: Timer,
              title: "Ghi chú và đánh giá trực tiếp",
              description: "Giúp học viên theo dõi tiến độ và cải thiện kết quả học tập."
            }
          ].map((criteria, index) => (
            <Card key={index} className="p-8 hover:border-primary/50 transition-colors">
              <criteria.icon className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3">{criteria.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{criteria.description}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Enhanced Learning Section
const EnhancedLearningVi = () => {
  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background to-accent/5">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-12">
          Trải Nghiệm Học Tập Nâng Cao
        </h2>
        
        <Card className="p-8 md:p-12 bg-card/80 backdrop-blur text-left">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Tại CSW, chúng tôi tin rằng việc học cần vượt qua những phương pháp truyền thống. Vì vậy, chúng tôi 
            mang đến một cách tiếp cận sáng tạo, kết hợp các công cụ và kỹ thuật tiên tiến để nâng cao hiệu quả học tập. 
            Với các tính năng như nền học tập mô phỏng không gian lý tưởng, bộ hẹn giờ tùy chỉnh để quản lý luồng học, 
            và phòng học trực tuyến thời gian thực, CSW tạo ra một môi trường cá nhân hóa, đem lại sự tập trung và hiệu 
            quả cao. Các kỹ thuật quản lý tư duy, theo dõi tiến độ và đặt mục tiêu giúp bạn biến mỗi buổi học thành một 
            hành trình đầy cảm hứng và thành công.
          </p>
        </Card>
      </div>
    </section>
  );
};

// CTA Section
const CTAVi = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted to-background" />
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-12 animate-fade-in">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Tham Gia Cộng Đồng</span>
            </div>
            
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Người Bạn Đồng Hành Học Tập Của Bạn{" "}
              <span className="text-gradient">Đang Chờ Đợi</span>
            </h2>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-2xl mx-auto font-light">
              CSW không chỉ là một nền tảng học tập—đó là một cộng đồng tận tâm với sự xuất sắc học thuật của bạn. 
              Tham gia ngay hôm nay để khám phá lợi ích từ hỗ trợ học tập 24/7, công cụ học tập tiên tiến, và mạng 
              lưới toàn cầu đầy động lực. Cùng nhau, chúng ta sẽ thay đổi cách bạn học tập và đạt được thành công.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-xl glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Join A Room Now
                <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
          
          <div className="pt-8 space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <p className="text-sm text-muted-foreground uppercase tracking-wider font-semibold">
              Được tin cậy bởi học viên trên toàn thế giới
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
              {['Sinh Viên Đại Học', 'Chuyên Gia', 'Nhà Sáng Tạo', 'Nhà Nghiên Cứu'].map((group) => (
                <div key={group} className="text-muted-foreground font-medium">
                  {group}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const IndexVi = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - Phòng Học Ảo | Không Gian Học Tập Trực Tuyến Miễn Phí | Online Workspace | Productivity Tool | Study Stream"
        description="Phòng học ảo miễn phí, nâng cao sự tập trung và thói quen học tập. Mở cửa 24/7, đồng hồ Pomodoro, học cùng sinh viên toàn cầu."
        keywords="phòng học ảo, học trực tuyến, study stream, pomodoro timer, học online"
        canonicalUrl="/vn/"
      />
      <Header />
      <main>
        <HeroVi />
        <MeetNewPeopleVi />
        <ProductivityToolsVi />
        <SupportVi />
        <ClassroomModelsVi />
        <SoftwareVi />
        <AloneSectionVi />
        <CriteriaVi />
        <EnhancedLearningVi />
        <CTAVi />
      </main>
      <Footer />
    </div>
  );
};

export default IndexVi;
